import { useMemo } from "react";

import { isAfter } from "date-fns";

import { Proposal } from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import useGetPortfolioPlanAssignments from "@fartherfinance/frontend/api/Investment/hooks/usePortfolioPlanAssignment";
import { PortfolioAssignmentParams } from "@fartherfinance/frontend/api/Investment/request/getPortfolioPlanAssignment";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

import { ALERT_MESSAGES } from "./ProposalAlerts/ProposalAlerts";
import useGetAllPhysicalAccounts from "./useGetAllPhysicalAccounts.hooks";
import useGetAllVirtualAccounts from "./useGetAllVirtualAccounts.hooks";

function useProposalAlerts(proposal: Proposal | undefined): string[] {
  const auth = useAdvisorRequestAuth();

  const accountsQuery = useGetAllVirtualAccounts(
    proposal?.portfolioPlanAssignment.accounts ?? []
  );

  // this could have duplicates, so we need to remove them out
  const physicalAccountIds = useMemo(
    () => [
      ...new Set(
        accountsQuery.data?.flatMap((account) =>
          account.physicalAccountIds.map((id) => id)
        )
      ),
    ],
    [accountsQuery.data]
  );

  const physicalAccountsData = useGetAllPhysicalAccounts(physicalAccountIds);

  // if any of the accounts are APEX, display the banner
  const hasApexAccount = useMemo(
    () =>
      physicalAccountsData.data?.some(
        (account) => account.custodian === "Apex"
      ),
    [physicalAccountsData.data]
  );

  const portfolioPlanAssignmentParams: PortfolioAssignmentParams | null =
    useMemo(() => {
      if (
        proposal?.portfolioPlanAssignment.planAssignmentId === undefined ||
        proposal?.portfolioPlanAssignment.portfolioPlanId === undefined
      ) {
        return null;
      }

      return {
        portfolioPlanAssignmentId:
          proposal?.portfolioPlanAssignment.planAssignmentId,
        abstractPortfolioId: proposal?.portfolioPlanAssignment.portfolioPlanId,
      };
    }, [
      proposal?.portfolioPlanAssignment.planAssignmentId,
      proposal?.portfolioPlanAssignment.portfolioPlanId,
    ]);

  const portfolioAssignmentData = useGetPortfolioPlanAssignments(
    portfolioPlanAssignmentParams,
    auth
  );

  const tradingGroupModified = useMemo(() => {
    if (portfolioAssignmentData.data === undefined || proposal === undefined) {
      return false;
    }

    const wasUpdatedAfter = isAfter(
      portfolioAssignmentData.data.updatedAt,
      proposal.createdAt
    );

    return wasUpdatedAfter;
  }, [portfolioAssignmentData.data, proposal]);

  const alertsArray = useMemo(() => {
    const alerts = [];
    if (hasApexAccount === true) {
      alerts.push(ALERT_MESSAGES.manualTradingApex);
    }

    if (tradingGroupModified === true) {
      alerts.push(ALERT_MESSAGES.tradingGroupModified);
    }
    return alerts;
  }, [hasApexAccount, tradingGroupModified]);

  return alertsArray;
}

export default useProposalAlerts;
